import Actions from 'actions'
import {
  STL_BY_REQUEST_PROVIDERS,
  StlProviders,
  TrackingModes,
  TreatmentSTLsStatuses,
  TxTrackerValidationSteps
} from 'components/Patients/TreatmentTracker/txTrackerConsts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { v4 as uuidv4 } from 'uuid'

/**
 * A Retention 'Other' set is finalized under the provider name the doctor types, so an
 * upload without one would land as a reference no model can read. Active 'Other' keeps
 * its own contract: the name is entered later by the internal team.
 */
export const isProviderNameMissing = ({ trackingMode, provider, manualProvider }) =>
  trackingMode === TrackingModes.Retention && provider === StlProviders.Other && !(manualProvider || '').trim()

export const UploadValidationOutcomes = {
  Proceed: 'proceed',
  BlockInvalidFiles: 'blockInvalidFiles',
  BlockWithMessage: 'blockWithMessage'
}

/**
 * Decides what a preUpload validation result does to the upload, per tracking mode.
 *
 * Retention fails closed on everything: its checks are load-bearing (an incomplete set
 * would finalize into a reference no scan can run against, hours after the doctor left).
 * The active plan deliberately keeps the legacy production semantics: the two errors the
 * legacy build handled still block, anything else, including an error this build has
 * never heard of or a validation call that failed, lets the upload proceed exactly as
 * production always has.
 */
export const resolveUploadValidationOutcome = ({ validationError, trackingMode, ValidationErrors }) => {
  if (!validationError) {
    return UploadValidationOutcomes.Proceed
  }

  if (validationError === ValidationErrors.InvalidFiles) {
    return UploadValidationOutcomes.BlockInvalidFiles
  }

  if (trackingMode === TrackingModes.Retention) {
    return UploadValidationOutcomes.BlockWithMessage
  }

  return validationError === ValidationErrors.DuplicatedStls
    ? UploadValidationOutcomes.BlockWithMessage
    : UploadValidationOutcomes.Proceed
}

/**
 * @param runTxTrackerValidations `Function from the useTxTrackerValidations hook, being used in different steps (gets a step parameter, like - "providerChanged/beforeUpload")`
 * @param ValidationErrors Consts for Validation Error options
 * @param mode Optional - provider | null/practice. Provider is when external provider (like spark) uploads the STLs in a dedicated public landing page we created for them. null/practice is the default - for practice
 * @param trackingMode Optional - 'retention' routes the upload to the patient's Retention reference stack instead of the active treatment plan. Default is the active flow.
 */
export default ({
  setIsUploaderComponentLoading,
  runTxTrackerValidations,
  ValidationErrors,
  patientId,
  treatmentId,
  treatmentType,
  txTrackerStls,
  mode,
  trackingMode
}) => {
  const DROPZONE_CONTAINER_ID = 'dropzoneContainer'
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [provider, setProvider] = useState(null)
  const [validationError, setValidationError] = useState(null)
  const [invalidFiles, setInvalidFiles] = useState(null)
  const [isUploadCompletedOpen, setIsUploadCompletedOpen] = useState(false)
  const [manualProvider, setManualProvider] = useState('')
  // The beforeupload hook is registered once per uploader injection, so it must read the
  // name through a ref to see what the doctor typed after registration.
  const manualProviderRef = useRef(manualProvider)
  manualProviderRef.current = manualProvider

  const isMissingProviderName = isProviderNameMissing({ trackingMode, provider, manualProvider })
  // Each Retention upload is a brand new reference set: the id is minted per upload session
  // and regenerated after every completed upload, so a second set never lands on the first.
  const [retentionReferenceId, setRetentionReferenceId] = useState(uuidv4)

  const isRetentionUpload = trackingMode === TrackingModes.Retention

  const driveUploaderId = useSelector(state => state.appReducer.appconfig?.webapp?.driveUploaderId)

  // The folder name is the identity contract with grin-drive-s3-synchronizer, which routes
  // the files and emits the matching event based on the label segment.
  const driveFolderName = useMemo(
    () =>
      isRetentionUpload
        ? `@txTrackerRetention@${patientId}@${treatmentId}@${retentionReferenceId}@${provider}`
        : `@txTracker@${patientId}@${treatmentId}@${provider}@${treatmentType}`,
    [isRetentionUpload, patientId, treatmentId, retentionReferenceId, provider, treatmentType]
  )

  const {
    status: treatmentStlsStatus,
    stls,
    provider: existingStlsProvider
  } = useMemo(() => JSON.parse(txTrackerStls || '{}'), [txTrackerStls])

  const handleRequestProviderSTLs = useCallback(
    () => dispatch(Actions.requestProviderStls({ patientId, provider })),
    [dispatch, patientId, provider]
  )

  const handleDriveUploaderInitEvent = useCallback(
    ({ status, results, instance }) => {
      setIsUploaderComponentLoading(false)

      document.getElementsByClassName('uploader__heading')[0].innerText = t(
        'dialogs.patientInfo.txTrackerSetup.dragAndDropHere'
      )

      instance.setCustomSubfolderName(driveFolderName)
      instance.setHook('beforeupload', async (done, files) => {
        // Belt and braces with the injection gate below: the uploader should not exist
        // while the name is missing, but a drop racing a cleared name must still block.
        if (isProviderNameMissing({ trackingMode, provider, manualProvider: manualProviderRef.current })) {
          return
        }

        const filesToUpload = Array.from(files).map(file => ({ name: file.name }))
        const { validationError, invalidFiles = [] } = await runTxTrackerValidations({
          provider,
          step: TxTrackerValidationSteps.PreUpload,
          files: filesToUpload,
          trackingMode
        })

        const outcome = resolveUploadValidationOutcome({ validationError, trackingMode, ValidationErrors })

        if (validationError) {
          trackEvent('STL Upload Validation Error', {
            validationError,
            provider,
            treatmentType,
            trackingMode,
            blocked: outcome !== UploadValidationOutcomes.Proceed,
            files: filesToUpload
          })
        }

        if (outcome === UploadValidationOutcomes.Proceed) {
          done()
          return
        }

        if (outcome === UploadValidationOutcomes.BlockInvalidFiles) {
          setInvalidFiles(invalidFiles)
          return
        }

        setValidationError(validationError)
      })
    },
    [
      setIsUploaderComponentLoading,
      t,
      driveFolderName,
      runTxTrackerValidations,
      provider,
      ValidationErrors,
      treatmentType,
      trackingMode
    ]
  )

  const handleDriveUploaderDoneEvent = useCallback(
    ({ status, results, instance }) => {
      const { folder } = results

      setIsUploadCompletedOpen(true)
      trackEvent('STL Upload Completed', {
        provider,
        treatmentType,
        trackingMode,
        totalFiles: results.files?.length,
        linkToDrive: results.folder.link
      })

      // The Retention reference stack on the treatment is owned by the backend (written
      // under a lock when the files land), so a Retention upload must not touch
      // txTrackerStls: that field is the active plan.
      if (!isRetentionUpload) {
        dispatch(
          Actions.updateTreatment({
            treatmentId,
            txTrackerStls: JSON.stringify({
              stls,
              status: TreatmentSTLsStatuses.PendingFilesTransfer,
              provider,
              latestUploadData: { date: new Date().toISOString(), stls: results?.files?.map(file => file.name) }
            })
          })
        )
      }

      dispatch(Actions.triggerCopyStlsFromGDriveToS3({ driveFolderName: folder.name, manualProvider }))

      if (isRetentionUpload) {
        // The next upload is a new reference set. Regenerating the id also re-injects the
        // uploader with a fresh folder name via the effect below.
        setRetentionReferenceId(uuidv4())
      }
    },
    [provider, treatmentType, trackingMode, isRetentionUpload, dispatch, treatmentId, stls, manualProvider]
  )

  const driveUploaderCallbackFunc = useCallback(
    (status, results, instance) => {
      switch (status) {
        case 'init': {
          handleDriveUploaderInitEvent({ status, results, instance })
          break
        }
        case 'done': {
          handleDriveUploaderDoneEvent({ status, results, instance })
          break
        }
        default: {
          break
        }
      }
    },
    [handleDriveUploaderDoneEvent, handleDriveUploaderInitEvent]
  )

  // Unfortunately we have to do all kind of shit in order to work with the DriveUploader component.
  // For each provider change we need to completely unload and reload the DriveUploader component & Script in order for the
  // 'init' event to be called and set the relevant subFolderName (which relies on provider and patient's state)
  useEffect(() => {
    const existingDriveUploaderScript = document.getElementById('driveUploaderScript')
    const driveUploaderComponent = document.getElementById('driveUploaderComponent')

    if (existingDriveUploaderScript) {
      existingDriveUploaderScript.remove()
      window.driveUploaderCallbackFunc = null
      driveUploaderComponent?.remove()
    }
    if (
      (!validationError && provider && !isMissingProviderName && !STL_BY_REQUEST_PROVIDERS.includes(provider)) ||
      mode === 'provider'
    ) {
      const head = document.querySelector('head')
      const driveUploaderScript = document.createElement('script')
      const driveUploaderCallbackScript = document.createElement('script')

      driveUploaderScript.id = 'driveUploaderScript'
      window.driveUploaderCallbackFunc = driveUploaderCallbackFunc
      driveUploaderScript.src = `https://driveuploader.com/upload/${driveUploaderId}/embed.js?callback=driveUploaderCallbackFunc`

      driveUploaderCallbackScript.innerHTML = driveUploaderCallbackFunc.toString()

      const fileUploadDivToAppend = document.createElement('div')
      fileUploadDivToAppend.id = 'driveUploaderComponent'
      fileUploadDivToAppend.className = 'driveuploader-fill'
      fileUploadDivToAppend.setAttribute('style', 'height: 100%; width: 100%')

      const dropzone = document.getElementById(DROPZONE_CONTAINER_ID)

      dropzone.appendChild(fileUploadDivToAppend)
      head.appendChild(driveUploaderScript)
      head.appendChild(driveUploaderCallbackScript)
    }
  }, [
    driveUploaderCallbackFunc,
    provider,
    driveUploaderId,
    mode,
    DROPZONE_CONTAINER_ID,
    validationError,
    isMissingProviderName
  ])

  return {
    DROPZONE_CONTAINER_ID,
    provider,
    setProvider,
    validationError,
    setValidationError,
    invalidFiles,
    setInvalidFiles,
    manualProvider,
    setManualProvider,
    isMissingProviderName,
    isUploadCompletedOpen,
    setIsUploadCompletedOpen,
    handleRequestProviderSTLs,
    treatmentStlsStatus,
    stls,
    existingStlsProvider
  }
}
